import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  QuestionBase,
  DropdownQuestion,
  ImageUploaderSmall,
  ImageUploaderLarge,
  ImageUploaderPositioner,
  QuestionGroup,
  TextboxQuestion,
  TextareaQuestion,
  DocumentsUploader,
  HiddenQuestion,
  Decorator,
  CheckboxQuestion,
  WysiwygQuestion,
  ListHelperService,
  CoursesService,
  Itinerary,
  Client
} from 'core/index';
import { ClientsService, ProfileService } from 'core/services';
import { CoursesQuestion } from 'core/components/forms/courses-question';
import { PublishQuestion } from 'core/components/forms/publish-question';
import { TranslateService } from '@ngx-translate/core';
import { PUBLISH_DEFAULT, DEFAULT_CLIENT } from '../assets/constants';
import { CoreQuestionsFactoryService } from 'core/services/core-questions-factory.service';
import { ROLES } from 'core/services/assets/constants';

@Injectable()
export class ItinerariesQuestionsService {
  protected loggedUser: any;

  constructor(
    protected factory: CoreQuestionsFactoryService,
    protected clientsService: ClientsService,
    protected profileService: ProfileService,
    protected coursesService: CoursesService,
    protected translate: TranslateService,
    protected listHelper: ListHelperService
  ) {
    profileService.getLoggedUser$().subscribe((loggedUser) => {
      this.loggedUser = loggedUser;
    });
  }

  public getQuestions(params: any = {}) {
    const { itinerary, client: cli } = params;
    const clientView = !!cli;
    const client = this.getItineraryClient(params);
    const isItineraryOwner = this.isUserItineraryOwner(itinerary);
    const canEditItinerary = this.canUserEditItinerary(itinerary, cli);
    const newParams = { ...params, client, canEditItinerary, isItineraryOwner, clientView };

    return canEditItinerary ? this.getCreatorQuestions(newParams) : this.getUserQuestions(newParams);
  }

  public canUserEditItinerary(itinerary: Itinerary | any, client: Client | any): boolean {
    const clientView = !!client; // true on client detail or itineraries menu for client admin
    return (
      !itinerary || // create itinerary
      (!clientView && this.isGamelearnAdmin()) || // itineraries menu and gamelearn admin
      (!clientView && this.isPartnerAdmin() && this.isUserItineraryOwner(itinerary)) || // itineraries menu and partner admin and partner itinerary
      (clientView && itinerary.clientId === client.id && !itinerary.publish.scope) // client admin and client itinerary
    );
  }

  private isUserItineraryOwner(itinerary): boolean {
    return (
      !itinerary || // create itinerary
      (this.isGamelearnAdmin && itinerary.clientId === DEFAULT_CLIENT.id) || // is gamelearn itinerary
      itinerary.clientId === this.loggedUser.clientId // is a partner or client itinerary
    );
  }

  private isClientAdmin(): boolean {
    return [ROLES.DACH_ADMIN, ROLES.CLIENT_ADMIN].includes(this.loggedUser.role);
  }

  private isPartnerAdmin(): boolean {
    return [ROLES.DISTRIBUTOR, ROLES.EXPERT].includes(this.loggedUser.role);
  }

  private isGamelearnAdmin(): boolean {
    return [ROLES.SUPER_ADMIN, ROLES.CSADMIN, ROLES.CSM, ROLES.SUPPORT_SPECIALIST].includes(this.loggedUser.role);
  }

  private getItineraryClient(params: any) {
    const { itinerary, client: cli, partnerClient } = params;
    const client = cli
      ? cli
      : itinerary
      ? { id: itinerary.clientId, name: itinerary.clientName }
      : this.isGamelearnAdmin
      ? DEFAULT_CLIENT
      : this.isPartnerAdmin
      ? partnerClient
      : null;
    return client;
  }

  private getUserQuestions(params: any = {}) {
    const { itinerary, courses, clientView } = params;

    const accessModeQuestion = clientView ? this.getAccessModeQuestion(params) : null;
    const creatorQuestion = this.createDecoratorGroup('nouns.creator', itinerary.clientName);
    const durationQuestion = this.createDecoratorGroup(
      'nouns.duration',
      this.listHelper.parseCourseDuration(itinerary)
    );
    creatorQuestion.class = 'grid-columns-area-4';
    durationQuestion.class = 'grid-columns-area-8';

    const itineraryInfoGroup: QuestionGroup = new QuestionGroup({
      title: itinerary.title || itinerary.name,
      key: '_itineraryConfig',
      gridClass: 'grid-columns-12',
      childQuestions: [
        this.getWithdrawDecorator(params),
        accessModeQuestion,
        creatorQuestion,
        durationQuestion,
        this.createDecoratorGroup('itineraries.description', itinerary.description),
        this.createDecoratorGroup('itineraries.objetives', itinerary.objectives, true),
        this.createDecoratorGroup('itineraries.contents', itinerary.contents, true),
        new CoursesQuestion({
          key: 'modules',
          label: 'catalog.label',
          class: 'grid-columns-area-12',
          value: itinerary ? itinerary.modules : [],
          courses,
          disabled: true,
          hideButtons: true,
          hideTitle: true,
          unavailableCourses: itinerary ? itinerary.unavailableCoursesforClients : [],
          validators: {
            function: (formControl: UntypedFormControl) => {
              return formControl.value && formControl.value.length >= 2 ? null : { courseMatch: false };
            }
          },
          hideLabel: true
        }),
        this.getDiplomaQuestionGroup(params)
      ].filter((q) => q)
    });
    return [itineraryInfoGroup];
  }

  private createDecoratorGroup(title: string, value: string, isHtml: boolean = false) {
    return new QuestionGroup({
      disableGrid: true,
      class: 'grid-columns-area-12',
      childQuestions: [
        new Decorator({
          value: title,
          valueClass: 'itineraries-form__title'
        }),
        new Decorator({
          value: !isHtml ? value : null,
          wysiwyg: isHtml ? value : null
        })
      ]
    });
  }

  private getWithdrawDecorator(params: any = {}) {
    const { itinerary } = params;
    return new Decorator({
      class: 'grid-columns-area-12',
      html: `<span class="glicon-alert-thin"></span><span>${this.translate.instant(
        'itineraries.config.messageDrawItinerary'
      )}</span>`,
      valueClass: 'gl-forms__alert--decorator',
      hidden: !itinerary || !itinerary.withdrawn
    });
  }

  private getCreatorQuestions(params: any = {}) {
    const questions: QuestionBase<any>[] = [this.getItineraryInfoGroup(params), this.getItineraryConfigGroup(params)];
    return questions;
  }

  /**
   * Group of questions getting the basic info of itinerary
   * @param params Object with itinerary, client and course properties
   * @returns Question Group object
   */
  private getItineraryInfoGroup(params: any = {}): QuestionGroup {
    const { itinerary, client, canEditItinerary } = params;

    const itineraryInfoGroup: QuestionGroup = new QuestionGroup({
      title: 'itineraries.infoTitle',
      isSection: true,
      key: '_itineraryInfo',
      class: 'grid-columns-area-12',
      childQuestions: [
        this.getWithdrawDecorator(params),
        this.factory.getClientQuestion({
          client,
          label: 'nouns.creator',
          class: 'grid-columns-area-12',
          value: itinerary ? itinerary.clientId : client.id,
          validators: { required: true },
          editable: false,
          childQuestions: [
            {
              key: 'clientName',
              value: itinerary ? itinerary.clientName : client.name,
              source: 'name'
            }
          ]
        }),
        new TextboxQuestion({
          key: 'name',
          label: 'itineraries.name',
          placeholder: 'itineraries.namePlaceholder',
          value: itinerary ? (canEditItinerary ? itinerary.name : itinerary.title || itinerary.name) : null,
          class: 'grid-columns-area-12',
          validators: {
            required: true,
            maxLength: 100
          }
        }),
        new DropdownQuestion({
          key: 'lang',
          label: 'nouns.baseLang',
          placeholder: 'actions.selectLanguage',
          class: 'grid-columns-area-4-offset',
          value: itinerary ? itinerary.lang : null,
          options: [
            { key: 'en', value: this.translate.instant('languages.english') },
            { key: 'es', value: this.translate.instant('languages.spanish') },
            { key: 'fr', value: this.translate.instant('languages.french') },
            { key: 'de', value: this.translate.instant('languages.german') },
            { key: 'pt', value: this.translate.instant('languages.portuguese') },
            { key: 'nl', value: this.translate.instant('languages.dutch') }
          ],
          validators: { required: true }
        }),
        new TextareaQuestion({
          key: 'description',
          label: 'itineraries.description',
          class: 'grid-columns-area-12',
          placeholder: 'itineraries.descriptionPlaceholder',
          validators: {
            required: true,
            maxLength: 1000
          },
          heightBox: 207,
          value: itinerary ? itinerary.description : null
        }),
        new WysiwygQuestion({
          key: 'objectives',
          label: 'itineraries.objetives',
          class: 'grid-columns-area-12',
          placeholder: 'itineraries.objetivesPlaceholder',
          value: itinerary ? itinerary.objectives : null
        }),
        new WysiwygQuestion({
          key: 'contents',
          label: 'itineraries.contents',
          class: 'grid-columns-area-12',
          placeholder: 'itineraries.contentsPlaceholder',
          value: itinerary ? itinerary.contents : null
        }),
        this.isGamelearnAdmin &&
        ((itinerary && itinerary.clientId === DEFAULT_CLIENT.id) || client.id === DEFAULT_CLIENT.id)
          ? this.getDocumentsQuestions(itinerary)
          : null
      ]
    });
    return itineraryInfoGroup;
  }

  private getDocumentsQuestions(itinerary: any = {}) {
    const documentation = itinerary && itinerary.documentation ? itinerary.documentation : {};
    const documentationQuestion = new QuestionGroup({
      key: 'documentation',
      class: 'grid-columns-area-12',
      childQuestions: [
        new Decorator({
          value: 'itineraries.didacticGuide',
          tooltip: 'groups.config.documentation.info',
          tooltipKey: 'groups.config.documentation',
          valueClass: 'heading-form',
          class: 'no-margin'
        }),
        new HiddenQuestion({
          key: 'hasChanges',
          value: false
        }),
        new HiddenQuestion({
          key: 'type',
          value: documentation.type
        }),
        new DocumentsUploader({
          key: '_document',
          maxSizeInMB: 5,
          value: documentation.url,
          class: 'grid-columns-area-12',
          eventHandlers: {
            onSelected: (value) => {
              (documentationQuestion.control as UntypedFormGroup).controls['hasChanges'].setValue(true);
              (documentationQuestion.control as UntypedFormGroup).controls['type'].setValue('pdf');
              (documentationQuestion.control as UntypedFormGroup).controls['url'].setValue(value ? value.name : '');
            }
          }
        }),
        new HiddenQuestion({
          key: 'url',
          value: documentation.url
        })
      ]
    });
    return documentationQuestion;
  }

  /**
   * Group of questions getting the config info of itinerary
   * @param params Object with itinerary, client and course properties
   * @returns Question Group object
   */
  private getItineraryConfigGroup(params: any = {}): QuestionGroup {
    const { itinerary, client, courses, clientView } = params;
    const isGamelearnItinerary = client.id === DEFAULT_CLIENT.id;
    const canGetBonus = itinerary ? this.coursesService.canModulesGetBonus(itinerary.modules) : false;
    const canSetBonusRole =
      this.loggedUser.role && this.loggedUser.role !== 'csm' && this.loggedUser.role !== 'supportSpecialist'
        ? true
        : false;
    let data: any = {
      bannerMobilePosition: 0
    };
    if (itinerary) {
      data = {
        ...data,
        bannerMobilePosition: itinerary.bannerMobilePosition,
        bannerUrl: itinerary.bannerUrl,
        cardImageUrl: itinerary.cardImageUrl
      };
    }

    const configAccessQuestion = clientView ? this.getAccessModeQuestion(params) : this.getPublishQuestion(params);

    const itineraryConfigGroup: QuestionGroup = new QuestionGroup({
      title: 'itineraries.configCreation',
      isSection: true,
      key: '_itineraryConfig',
      class: 'grid-columns-area-12',
      childQuestions: [
        configAccessQuestion,
        new ImageUploaderSmall({
          key: 'cardImg',
          label: 'itineraries.imageTitle',
          class: 'grid-columns-area-12',
          value: data.cardImageUrl ? data.cardImageUrl : null,
          maxSizeMb: 20,
          validators: {
            function: (formControl: UntypedFormControl) => {
              if (data.cardImageUrl) {
                return formControl.value.length < 2 ? { imagesMatch: false } : null;
              } else {
                return !formControl.value ? { imagesMatch: false } : null;
              }
            }
          }
        }),
        new ImageUploaderLarge({
          key: 'bannerImg',
          label: 'itineraries.imageTitle',
          class: 'grid-columns-area-12',
          value: data.bannerUrl ? data.bannerUrl : null,
          maxSizeMb: 20,
          validators: {
            function: (formControl: UntypedFormControl) => {
              if (data.bannerUrl) {
                return formControl.value.length < 2 ? { imagesMatch: false } : null;
              } else {
                return !formControl.value ? { imagesMatch: false } : null;
              }
            }
          }
        }),
        new ImageUploaderPositioner({
          key: 'bannerMobilePosition',
          label: 'itineraries.imageTitle',
          class: 'grid-columns-area-12',
          value: data.bannerMobilePosition ? data.bannerMobilePosition : null,
          order: 10
        }),
        new CoursesQuestion({
          key: 'modules',
          label: 'actions.selectedCourse',
          class: 'grid-columns-area-12',
          value: itinerary ? itinerary.modules : null,
          courses,
          hideButtons: itinerary?.publish?.active,
          unavailableCourses: itinerary ? itinerary.unavailableCoursesforClients : [],
          validators: {
            function: (formControl: UntypedFormControl) => {
              return formControl.value && formControl.value.length >= 2 ? null : { courseMatch: false };
            }
          },
          hideLabel: true,
          eventHandlers: {
            onSelected: (courses) => {
              const bonusPosibleQuestion = itineraryConfigGroup.getChildQuestion('bonusPossible');
              const diplomaQuestion = itineraryConfigGroup
                .getChildQuestion('configuration')
                .getChildQuestion('shouldSendDiploma');
              const hideCheckboxes = !courses || courses.length === 0;
              bonusPosibleQuestion.hidden = !isGamelearnItinerary || hideCheckboxes;
              diplomaQuestion.hidden = hideCheckboxes;
              if (hideCheckboxes) {
                bonusPosibleQuestion.control.setValue(false);
                diplomaQuestion.control.setValue(false);
              }
              const canGetBonus = this.coursesService.canModulesGetBonus(courses);
              if (!canGetBonus) {
                bonusPosibleQuestion.control.setValue(false);
              }
              bonusPosibleQuestion.setEditable(canGetBonus);
            }
          }
        }),
        this.getDiplomaQuestionGroup(params),
        new CheckboxQuestion({
          key: 'bonusPossible',
          value: itinerary ? itinerary.bonusPossible : false,
          label: 'itineraries.fundae',
          class: 'grid-columns-area-12',
          hidden: !isGamelearnItinerary || !itinerary,
          editable: canGetBonus && canSetBonusRole, // canSetBonusRole to csm exception
          eventHandlers: {
            onChange: (checked) => {
              const coursesQuestion = itineraryConfigGroup.getChildQuestion('modules');
              const diplomaQuestion = itineraryConfigGroup
                .getChildQuestion('configuration')
                .getChildQuestion('shouldSendDiploma');
              diplomaQuestion.setEditable(!checked);
              coursesQuestion.courses = checked ? courses.filter(this.coursesService.canModuleGetBonus) : courses;
              if (checked) {
                diplomaQuestion.control.setValue(checked);
              } else {
                diplomaQuestion.control.setValue(false);
              }
            }
          }
        })
      ]
    });

    return itineraryConfigGroup;
  }

  private getAccessModeQuestion(params: any): QuestionBase<any> {
    const { itinerary, defaultAccessMode } = params;
    return new DropdownQuestion({
      class: 'grid-columns-area-4-offset',
      key: 'accessMode',
      label: 'courses.config.accessMode',
      value: itinerary && itinerary.accessMode ? itinerary.accessMode : 'default',
      options: [
        {
          key: 'default',
          value: this.translate.instant('courses.config.accessModes.default', {
            mode: this.translate.instant(`courses.config.accessModes.${defaultAccessMode || 'open'}`)
          })
        },
        {
          key: 'open',
          value: this.translate.instant('courses.config.accessModes.open')
        },
        { key: 'limited', value: this.translate.instant('courses.config.accessModes.limited') },
        { key: 'unavailable', value: this.translate.instant('courses.config.accessModes.unavailable') }
      ]
    });
  }

  private getPublishQuestion(params: any): QuestionBase<any> {
    const { itinerary, client, isItineraryOwner } = params;
    const value = itinerary
      ? itinerary.publish
      : {
          ...PUBLISH_DEFAULT,
          scope: this.isPartnerAdmin ? this.profileService.getFixedPartnerId() : PUBLISH_DEFAULT.scope
        };
    return new PublishQuestion({
      key: 'publish',
      label: 'courses.config.clientsWithAccess',
      class: 'grid-columns-area-6-offset',
      value,
      owner: itinerary ? itinerary.clientName : client.name,
      selector: isItineraryOwner,
      editable: isItineraryOwner
    });
  }

  private getDiplomaQuestionGroup(params: any = {}): QuestionBase<any> {
    const { itinerary, clientView, canEditItinerary } = params;
    return new QuestionGroup({
      key: 'configuration',
      childQuestions: [
        new CheckboxQuestion({
          key: 'shouldSendDiploma',
          label: 'itineraries.config.diploma',
          class: 'grid-columns-area-12',
          value:
            !itinerary || !itinerary.configuration || !itinerary.configuration.shouldSendDiploma
              ? false
              : itinerary.configuration.shouldSendDiploma,
          hidden: !itinerary,
          disabled: !(clientView || canEditItinerary)
        })
      ]
    });
  }
}
